import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { OfferEvent } from '@/lib/types';

interface EventWithOffer extends OfferEvent {
  offer: { name: string } | null;
}

const EVENT_LABELS: Record<string, { label: string; icon: string }> = {
  created: { label: 'Oferta creada', icon: 'ri-add-circle-line' },
  updated: { label: 'Oferta modificada', icon: 'ri-edit-line' },
  product_added: { label: 'Producto agregado', icon: 'ri-add-box-line' },
  product_removed: { label: 'Producto retirado', icon: 'ri-delete-bin-6-line' },
  activated: { label: 'Oferta activada', icon: 'ri-play-circle-line' },
  finalized: { label: 'Oferta finalizada', icon: 'ri-archive-line' },
  cancelled: { label: 'Oferta cancelada', icon: 'ri-close-circle-line' },
};

export default function OfferHistory() {
  const [items, setItems] = useState<EventWithOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('offer_events')
        .select('*, offer:offers(name)')
        .order('created_at', { ascending: false })
        .limit(200);
      if (e) throw e;
      setItems((data as EventWithOffer[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el historial');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <p className="text-sm text-foreground-600 mb-4">
        Registro de cambios en las ofertas (creación, edición, productos y estado).
      </p>

      {error && (
        <div className="mb-4 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-history-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">Aún no hay movimientos de ofertas.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-background-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-foreground-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Evento</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Oferta</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Usuario</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Detalle</th>
                <th className="px-4 py-3 font-medium">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {items.map((ev) => {
                const meta = EVENT_LABELS[ev.event_type] ?? { label: ev.event_type, icon: 'ri-information-line' };
                const detail = ev.details as { name?: string } | null;
                return (
                  <tr key={ev.id} className="hover:bg-background-50">
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 font-medium text-foreground-900">
                        <i className={`${meta.icon} text-foreground-400`} />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground-700 hidden md:table-cell">
                      {ev.offer?.name ?? detail?.name ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-foreground-700 hidden lg:table-cell">
                      {ev.user_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-foreground-500 hidden lg:table-cell">
                      {detail?.name ? String(detail.name) : '—'}
                    </td>
                    <td className="px-4 py-3 text-foreground-500">
                      {new Date(ev.created_at).toLocaleString('es-DO')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}